import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { UserRole } from "@/models/user";

// Validación de email más estricta para Supabase
const formSchema = z.object({
  fullName: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  email: z.string()
    .min(1, "El email es requerido")
    .email("Formato de email inválido"), // Usar validación estándar de email de Zod
  password: z.string().min(6, "La contraseña debe tener al menos 6 caracteres"),
  role: z.enum(["admin", "seller", "contabilidad", "mantenimiento"], {
    required_error: "Por favor selecciona un rol"
  })
});

type FormValues = z.infer<typeof formSchema>;

interface CreateUserFormProps {
  onUserCreated: () => void;
}

// Validación de email más estricta
const isValidEmail = (email: string): boolean => {
  // Expresión regular más completa para validar emails
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const CreateUserForm: React.FC<CreateUserFormProps> = ({ onUserCreated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const [timeUntilNextSubmit, setTimeUntilNextSubmit] = useState(0);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      role: undefined
    }
  });

  // Effect to update countdown timer
  useEffect(() => {
    if (lastSubmitTime === 0) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastSubmit = now - lastSubmitTime;
      const minTimeInterval = 5000; // 5 seconds
      const remaining = Math.max(0, Math.ceil((minTimeInterval - timeSinceLastSubmit) / 1000));
      
      setTimeUntilNextSubmit(remaining);
      
      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastSubmitTime]);

  const handleSubmit = async (data: FormValues) => {
    // Prevent rapid submissions (minimum 5 seconds between submissions)
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitTime;
    const minTimeInterval = 5000; // 5 seconds
    
    if (timeSinceLastSubmit < minTimeInterval) {
      const remainingTime = Math.ceil((minTimeInterval - timeSinceLastSubmit) / 1000);
      toast.error(`Por favor espera ${remainingTime} segundos antes de crear otro usuario.`);
      return;
    }
    
    setIsSubmitting(true);
    setLastSubmitTime(now);
    
    try {
      const normalizedEmail = data.email.trim().toLowerCase();
      console.log("Intentando crear usuario con email:", normalizedEmail);
      
      // Validación adicional de email
      if (!isValidEmail(normalizedEmail)) {
        throw new Error("El formato del correo electrónico no es válido. Debe tener el formato: usuario@dominio.com");
      }
      
      // 1. Registrar el usuario con el método estándar signUp
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
            role: data.role
          }
        }
      });
      
      if (authError) {
        console.error("Error detallado de autenticación:", {
          message: authError.message,
          status: authError.status,
          name: authError.name,
          details: authError
        });
        
        if (authError.message.includes("Database error saving new user")) {
          toast.error("Error al crear el usuario en la base de datos. Por favor, verifica que:");
          setTimeout(() => {
            toast.info("- El correo electrónico no esté ya registrado\n- El formato del correo sea válido\n- La contraseña cumpla los requisitos mínimos");
          }, 1000);
          throw new Error("Error en la base de datos al guardar el usuario");
        }
        
        throw authError;
      }
      
      // 2. Verificar que el usuario se haya creado correctamente
      if (!authData.user) {
        throw new Error("No se pudo crear el usuario");
      }
      
      console.log("Usuario creado con ID:", authData.user.id);
      
      // 3. Esperar un momento para asegurar que el usuario esté disponible en la base de datos
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 4. Asegurarnos de que el perfil del usuario exista
      const { data: existingProfile, error: profileCheckError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .single();
      
      if (profileCheckError && !profileCheckError.message.includes('No rows found')) {
        console.error("Error al verificar el perfil:", profileCheckError);
      }
      
      // 5. Si el perfil no existe, crearlo manualmente
      if (!existingProfile) {
        console.log("Creando perfil manualmente para el usuario:", authData.user.id);
        
        const { error: insertError } = await supabase
          .from('profiles')
          .upsert({
            id: authData.user.id,
            full_name: data.fullName,
            email: normalizedEmail,
            role: data.role
          }, {
            onConflict: 'id'
          });
        
        if (insertError) {
          console.error("Error al crear perfil:", insertError);
          // No lanzar error aquí, el usuario ya fue creado en auth
          console.warn("El usuario fue creado en auth pero no se pudo crear su perfil automáticamente");
        } else {
          console.log("Perfil creado correctamente");
        }
      } else {
        console.log("El perfil ya existe:", existingProfile);
      }
      
      // 6. Mensaje de confirmación
      if (authData.user.email_confirmed_at) {
        toast.success("Usuario creado exitosamente. Puede iniciar sesión inmediatamente.");
      } else {
        toast.success("Usuario creado exitosamente. Se ha enviado un correo de confirmación, pero puede intentar iniciar sesión directamente.");
      }
      
      // Reset form
      form.reset({
        fullName: "",
        email: "",
        password: "",
        role: data.role
      });
      
      // Refresh users list
      onUserCreated();
    } catch (error) {
      console.error("Error creating user:", error);
      
      // Handle specific errors
      if (error instanceof Error && error.message.includes("For security purposes")) {
        const waitTime = error.message.match(/(\d+) seconds?/)?.[1] || "30";
        toast.error(`Límite de velocidad alcanzado. Por favor espera ${waitTime} segundos antes de intentar nuevamente.`);
      } else if (error instanceof Error && error.message.includes("User already registered")) {
        toast.error("Ya existe un usuario con este correo electrónico.");
      } else if (error instanceof Error && error.message.includes("User not allowed")) {
        toast.error("Error de permisos. Contacta al administrador del sistema.");
      } else if (error instanceof Error && (error.message.includes("is invalid") || error.message.includes("Email"))) {
        // Mostrar mensaje más descriptivo sobre el error de validación de email
        console.error("Error de validación de email:", error);
        toast.error("El formato del correo electrónico no es válido para Supabase Auth.");
        
        // Mostrar información adicional sobre el formato esperado
        setTimeout(() => {
          toast.info("Por favor, utiliza un formato de email estándar como usuario@dominio.com");
        }, 1000);
      } else {
        // Show detailed error for debugging
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error("Detailed error:", error);
        toast.error(`Error al crear usuario: ${errorMessage}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre completo</FormLabel>
              <FormControl>
                <Input placeholder="Nombre Apellido" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Correo electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="usuario@dominio.com" {...field} />
              </FormControl>
              <FormMessage />
              <p className="text-xs text-muted-foreground">
                Usar un formato de email estándar (ejemplo: usuario@dominio.com)
              </p>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contraseña</FormLabel>
              <FormControl>
                <Input type="password" placeholder="******" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Rol</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
                required
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un rol" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="seller">Vendedor</SelectItem>
                  <SelectItem value="contabilidad">Contabilidad</SelectItem>
                  <SelectItem value="mantenimiento">Enc Mantenimiento</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
              <p className="text-xs text-muted-foreground">
                El rol determina los permisos del usuario en el sistema
              </p>
            </FormItem>
          )}
        />
        
        <Button 
          type="submit" 
          disabled={isSubmitting || timeUntilNextSubmit > 0}
          className="w-full flex items-center gap-2"
        >
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {timeUntilNextSubmit > 0 ? `Espera ${timeUntilNextSubmit}s` : "Crear usuario"}
        </Button>
      </form>
    </Form>
  );
};

export default CreateUserForm;
